import { useI18n } from "react-simple-i18n";
import { styled } from "@mui/material/styles";
import { Box, Modal, Typography } from "@mui/material";
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';

import { restApi } from "../../provider/restApi";
import { toastMessage } from "../../utils";
import { useGlobalContext } from "../../provider";
import { apiNotification } from "../../services/apiNotification";
import { FirstOutlineButton, ThirdOutlineButton } from "../../components/buttons/buttons";

interface PaymentFormProps {
	open: boolean
	data: CourseDetailObject
	onCancel: () => void
}

const PaymentForm = ({ open, data, onCancel }: PaymentFormProps) => {
	const { t } = useI18n();
	const stripe = useStripe();
	const elements = useElements();
	const [globalState, { dispatch }]: GlobalContextType = useGlobalContext();

	const handleSubmit = async (event: React.FormEvent) => {
		try {
			event.preventDefault();
			if(!stripe || !elements) return

			dispatch({ loading: true });
			const { clientSecret, status } = await restApi.createPaymentIntent(data.id);
			
			if(status === "succeeded") {
				toastMessage("info", "Successfully purchased!");
				onCancel();
				dispatch({loading:false});
				return;
			}
			
			// Confirm the payment
			await stripe.confirmCardPayment(clientSecret, {
				payment_method: { card: elements.getElement(CardNumberElement)! }
			})

			

			onCancel();
			dispatch({ loading: false });
			toastMessage("success", "Subscribe successed!");
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Subscribe failed!");
		}
	}

	return (
		<Modal open={open}>
			<PaymentFormWrapper onSubmit={handleSubmit}>
				<Typography variant="h3">
					{t("courseDetails.subscribeConfirmTitle")}
				</Typography>

				<Typography variant="body1" component="h6">
					{t("courseDetails.subscribeConfirmDescription")}
				</Typography>

				<CardElementContainer>
					<Typography variant="body2">
						Card Number
					</Typography>

					<CardElementBorder>
						<CardNumberElement />
					</CardElementBorder>
				</CardElementContainer>

				<CardElementWrapper>
					<CardElementContainer>
						<Typography variant="body2">
							Expiry Date
						</Typography>

						<CardElementBorder>
							<CardExpiryElement />
						</CardElementBorder>
					</CardElementContainer>

					<CardElementContainer>
						<Typography variant="body2">
							CVC
						</Typography>

						<CardElementBorder>
							<CardCvcElement />
						</CardElementBorder>
					</CardElementContainer>
				</CardElementWrapper>

				<ButtonsWrapper>
					<ThirdOutlineButton onClick={onCancel}>
						<Typography variant="body1">Cancel</Typography>
					</ThirdOutlineButton>

					<FirstOutlineButton type="submit" disabled={globalState.loading || !stripe}>
						<Typography variant="body1">Confirm</Typography>
					</FirstOutlineButton>
				</ButtonsWrapper>
			</PaymentFormWrapper>
		</Modal>
	)
}

const PaymentFormWrapper = styled("form")(({ theme }) => ({
	position: 'absolute',
	top: '50%', left: '50%',
	width: '90vw', maxWidth: '600px',
	transform: 'translate(-50%, -50%)',

	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),

	outline: 'none',
	padding: theme.spacing(3),
	borderRadius: theme.spacing(1),
	backgroundColor: theme.palette.common.mainBg,

	h6: {
		fontWeight: 400,
		color: `${theme.palette.common.firstColor}c0`
	},

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(2),
	}
}))

const CardElementWrapper = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
	gap: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const CardElementContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(0.5),

	p: {
		color: `${theme.palette.common.firstColor}c0`,
	}
}))

const CardElementBorder = styled(Box)(({ theme }) => ({
	borderRadius: 5,
	padding: theme.spacing(1),
	border: `1px solid ${theme.palette.common.firstColor}80`,
}))

const ButtonsWrapper = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	gap: theme.spacing(2),
	paddingTop: theme.spacing(1),

	button: {
		minWidth: 150,
		flex: 1,
	}
}))

export { PaymentForm }